import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { CanonicalUnit, Food, PhotoScanResult } from '@nutrigeist/shared';
import { foodApi, intakeApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { BarcodeScanner } from './BarcodeScanner.js';
import { LabelScanner } from './LabelScanner.js';
import { PhotoScanner } from './PhotoScanner.js';
import type { LoggableFood } from './LogFoodSheet.js';
import { DishEstimateCard } from './DishEstimateCard.js';
import { CameraIcon, SearchIcon } from './icons.js';
import { formatKcal } from '../lib/format.js';

/**
 * The three ways of picking a food — catalog search, barcode, nutrition label.
 *
 * Shared between logging a diary entry and adding an ingredient to a recipe.
 * Both need exactly the same acquisition paths, and keeping one implementation
 * means a fix to, say, the barcode scanner reaches both.
 *
 * Every panel reports its result through the same `onPicked` shape, so callers
 * do not care which route produced the food.
 */

export type FoodSource = 'search' | 'barcode' | 'label';

export interface PickedFood {
  food: LoggableFood;
  /** Consistency warnings from a label scan, if any. */
  warnings?: string[];
  /**
   * A quantity the panel already knows, so the log sheet opens on it.
   *
   * An estimated dish is the case that needs this: its numbers describe one
   * portion, and reopening at a default 100 g would silently rescale them.
   */
  quantity?: { amount: number; unit: CanonicalUnit };
}

/** Normalises a catalog food into the shape both callers work with. */
export function toLoggable(food: Food): LoggableFood {
  return {
    id: food.id,
    name: food.name,
    brand: food.brand,
    basis: food.basis,
    nutrients: food.nutrients,
    serving: food.serving,
    density: food.density,
  };
}

export function SearchPanel({ onPicked }: { onPicked: (picked: PickedFood) => void }): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ food: Food; score: number }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const search = useMutation({
    mutationFn: (input: { q: string; remote: boolean }) =>
      foodApi.search(input.q, { remote: input.remote, limit: 25 }),
    onSuccess: (data) => {
      setResults(data.results);
      setError(null);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Suche fehlgeschlagen.'),
  });

  return (
    <div className="stack">
      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          if (query.trim()) search.mutate({ q: query.trim(), remote: false });
        }}
      >
        <input
          className="input grow"
          placeholder="z. B. Magerquark"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button className="btn btn-primary" type="submit" disabled={search.isPending}>
          {search.isPending ? <span className="spinner" /> : <SearchIcon />}
        </button>
      </form>

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}

      {results.length > 0 && (
        <div className="card">
          <div className="entry-list">
            {results.map(({ food }) => (
              <button
                key={food.id}
                className="entry entry-button"
                onClick={() => onPicked({ food: toLoggable(food) })}
              >
                <div className="grow">
                  <div className="entry-name">{food.name}</div>
                  <div className="entry-meta">
                    {food.brand ? `${food.brand} · ` : ''}
                    {formatKcal(food.nutrients.kcal)} kcal / 100{' '}
                    {food.basis === 'per_100ml' ? 'ml' : 'g'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {search.isSuccess && results.length === 0 && (
        <div className="card empty-state stack">
          <span>Nichts im lokalen Katalog gefunden.</span>
          <button
            className="btn btn-ghost"
            disabled={search.isPending}
            onClick={() => search.mutate({ q: query.trim(), remote: true })}
          >
            Bei OpenFoodFacts suchen
          </button>
        </div>
      )}
    </div>
  );
}

export function BarcodePanel({
  onPicked,
  onScanLabel,
}: {
  onPicked: (picked: PickedFood) => void;
  /** Offered when a barcode is unknown; receives it so the label scan can
   *  attach it to the product it captures. */
  onScanLabel?: (barcode: string) => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [unknownBarcode, setUnknownBarcode] = useState<string | null>(null);
  const [scanning, setScanning] = useState(true);

  const lookup = useMutation({
    mutationFn: (barcode: string) => foodApi.byBarcode(barcode),
    onSuccess: (food) => onPicked({ food: toLoggable(food) }),
    onError: (caught, barcode) => {
      const notFound = caught instanceof ApiError && caught.status === 404;
      setUnknownBarcode(notFound ? barcode : null);
      setError(
        notFound
          ? 'OpenFoodFacts kennt dieses Produkt nicht.'
          : 'Suche fehlgeschlagen. Ist der Server erreichbar?',
      );
      setScanning(false);
    },
  });

  if (!scanning || lookup.isPending) {
    return (
      <div className="card stack">
        {lookup.isPending && (
          <div className="row">
            <span className="spinner" />
            <span>Produkt wird gesucht…</span>
          </div>
        )}
        {error && (
          <div className="banner banner-error" role="alert">
            {error}
          </div>
        )}

        {/* An unknown barcode is the exact moment the label scanner is useful,
            so offer it here rather than making the user find the tab. The code
            travels with it so the scanned label is filed under that barcode. */}
        {unknownBarcode && onScanLabel && (
          <button className="btn btn-primary btn-block" onClick={() => onScanLabel(unknownBarcode)}>
            <CameraIcon />
            Nährwerttabelle fotografieren
          </button>
        )}

        <button
          className="btn btn-ghost"
          onClick={() => {
            setError(null);
            setUnknownBarcode(null);
            setScanning(true);
          }}
        >
          Erneut scannen
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <BarcodeScanner
        onDetected={(barcode) => {
          setScanning(false);
          lookup.mutate(barcode);
        }}
        onClose={() => setScanning(false)}
      />
    </div>
  );
}

/**
 * Meal photo -> a list of suggested foods.
 *
 * Deliberately a picker rather than a single answer. Identification was
 * measured as good but not dependable: a Big Mac was named exactly on every
 * run, while a döner came back as "Panini" three times out of four at 0.9
 * confidence. Since every one of the model's guesses — dish name, alternatives
 * and visible ingredients — is resolved against the catalog, the right food is
 * usually still in this list even when the headline guess is wrong.
 */
export function PhotoPanel({
  onPicked,
  onCancel,
}: {
  onPicked: (picked: PickedFood) => void;
  onCancel: () => void;
}): JSX.Element {
  const [result, setResult] = useState<PhotoScanResult | null>(null);

  if (!result) {
    return (
      <div className="card">
        <PhotoScanner onScanned={setResult} onClose={onCancel} />
      </div>
    );
  }

  const { identification, candidates } = result;

  return (
    <div className="stack">
      <div className="card stack">
        <div className="row-between">
          <div className="grow">
            <div className="card-title" style={{ marginBottom: 4 }}>
              Erkannt
            </div>
            <strong>{identification.dishName ?? 'Unbekannt'}</strong>
          </div>
          <span className={identification.confidence >= 0.7 ? 'badge' : 'badge badge-warning'}>
            {Math.round(identification.confidence * 100)}%
          </span>
        </div>

        <div className="banner banner-info">
          Die Erkennung ist ein Vorschlag — prüfe sie. Die Menge gibst du im nächsten Schritt an.
        </div>

        <button className="btn btn-ghost btn-sm" onClick={() => setResult(null)}>
          Neues Foto
        </button>
      </div>

      <PhotoEstimate dishName={identification.dishName} onPicked={onPicked} />

      {candidates.length === 0 ? (
        <div className="card empty-state">
          Nichts im Katalog gefunden. Suche das Lebensmittel von Hand oder scanne den Barcode.
        </div>
      ) : (
        <div className="card">
          <div className="card-title">Vorschläge</div>
          <div className="entry-list">
            {candidates.map(({ term, match }) => (
              <button
                key={match.food.id}
                className="entry entry-button"
                onClick={() => onPicked({ food: toLoggable(match.food) })}
              >
                <div className="grow">
                  <div className="entry-name">{match.food.name}</div>
                  <div className="entry-meta">
                    {match.food.brand ? `${match.food.brand} · ` : ''}
                    {formatKcal(match.food.nutrients.kcal)} kcal / 100{' '}
                    {match.food.basis === 'per_100ml' ? 'ml' : 'g'} · erkannt als „{term}“
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function LabelPanel({
  onPicked,
  onCancel,
  barcode,
}: {
  onPicked: (picked: PickedFood) => void;
  onCancel: () => void;
  /** Set when the scan was reached from an unknown barcode; attached to the
   *  captured product so it resolves locally on the next scan. */
  barcode?: string | null;
}): JSX.Element {
  return (
    <div className="card">
      <LabelScanner
        onClose={onCancel}
        onScanned={(result) => {
          const { extraction } = result;
          onPicked({
            food: {
              id: null,
              name: extraction.productName?.trim() || 'Gescanntes Produkt',
              brand: extraction.brand,
              basis: extraction.basis,
              nutrients: extraction.nutrients,
              serving: extraction.serving,
              density: null,
              barcode: barcode ?? null,
            },
            warnings: result.warnings,
          });
        }}
      />
    </div>
  );
}

/**
 * A rough nutrient estimate for the dish the photo was identified as.
 *
 * Chained deliberately, and the chain matters: the photo produces a *name*, and
 * the name is what gets estimated. Nothing here reads a portion off the image.
 * That was measured and does not work — one unchanged döner photo produced
 * 250-650 kcal across four runs, and a Big Mac came out 197% heavy, because the
 * model matches a remembered template rather than reading scale off the plate.
 *
 * So what this shows is "a typical portion of Spaghetti Bolognese", not "the
 * plate in front of you". That is a genuinely useful default and a genuinely
 * different claim, and the caveat says so rather than leaving the number to be
 * read as a measurement.
 *
 * Run as its own query rather than folded into the scan response: identification
 * is fast and immediately useful, while the estimate costs a second model call
 * plus a catalog lookup per ingredient. Making the picker wait for it would
 * slow down the common case — picking a candidate — to serve the optional one.
 */
export function PhotoEstimate({
  dishName,
  onPicked,
}: {
  dishName: string | null;
  onPicked: (picked: PickedFood) => void;
}): JSX.Element | null {
  const estimate = useQuery({
    queryKey: ['intake', 'dish', dishName],
    queryFn: () => intakeApi.estimateDish({ name: dishName!, servings: 1 }),
    enabled: dishName != null && dishName.trim().length >= 2,
    retry: false,
    // The dish for a given name does not change between renders, and each miss
    // costs a model call.
    staleTime: 30 * 60 * 1000,
  });

  if (dishName == null) return null;

  if (estimate.isPending) {
    return (
      <div className="card row" style={{ gap: 10, alignItems: 'center' }}>
        <span className="spinner" role="status" aria-label="Schätzung lädt" />
        <span className="hint">Grobe Schätzung für „{dishName}“ wird berechnet …</span>
      </div>
    );
  }

  // A failed estimate is not an error worth showing: the candidate list below
  // is the primary path and is unaffected.
  if (estimate.isError || !estimate.data) return null;

  return (
    <DishEstimateCard
      dish={estimate.data}
      note={`Grobe Schätzung für eine übliche Portion „${dishName}“ — nicht für die Menge auf dem Foto. Aus einem Bild lässt sich kein Gewicht ablesen.`}
      // The card returns one portion; carrying that through is what stops the
      // log sheet reopening at a default 100 g and rescaling the estimate.
      onPick={(pick) => onPicked({ food: pick.food, quantity: pick.quantity })}
    />
  );
}
